import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTimeoutFn } from 'react-use';

import { authActions, authSelectors, authTypes } from 'app-components/flux/auth';
import { datasetsActions, datasetsSelectors } from 'app-components/flux/datasets';
import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { profileSelectors } from 'app-components/flux/profile';
import { userInfoActions, userInfoSelectors } from 'app-components/flux/user-info';
import { useFetchOnce, useLoadStates } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

import Dialog from '@mui/material/Dialog';
import { makeStyles } from 'tss-react/mui';

// custom
import SelectDatasetDialog from 'components/Dialogs/SelectDatasetDialog';
import ZeroStateView from 'components/ZeroStateView';

const useStyles = makeStyles()((theme) => ({
  dialogStyle: {
    zIndex: '1200 !important',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    width: '100%',
    maxHeight: '100%',
    height: 500,
    overflow: 'hidden',
    position: 'relative',
    borderRadius: 3,
  },
}));

const SetupPageAcme = () => {
  const dispatch = useDispatch();

  const { classes } = useStyles();

  const isAuthTokenValid = useSelector(authSelectors.isAuthTokenValid);
  const datasets = useSelector(datasetsSelectors.getActiveSortedDatasets);
  const entitledDatasetsLoadPending = useSelector(datasetsSelectors.getEntitledDatasetsLoadPending);
  const isDatasetsLoadPending = useSelector(datasetsSelectors.getLoadPending);
  const isDatasetsLoading = useSelector(datasetsSelectors.getIsLoading);
  const isProfileLoadPending = useSelector(profileSelectors.getLoadPending);
  const isProfileLoading = useSelector(profileSelectors.getIsLoading);
  const profile = useSelector(profileSelectors.getProfile);
  const dataset = useSelector(authSelectors.getCurrentDataset);
  const isUserEntitled = useSelector(userInfoSelectors.isEntitled);

  const [newDatasetClientId, setDatasetClientId] = useState(undefined);

  useFetchOnce(datasetsActions.getDatasets, datasetsSelectors, true, true);
  // fetch userinfo so we can check whether user is entitled to run Simplifi
  useFetchOnce(userInfoActions.getUserInfo, userInfoSelectors, false, true);

  const { isNoData } = useLoadStates(datasetsSelectors);

  const initNewDataset = useCallback(() => {
    const newDataset = datasetsSelectors.makeNewDataset(store.getState(), profile);
    tracker.track(tracker.events.datasetCreated);
    dispatch(datasetsActions.createDataset(newDataset));
    setDatasetClientId(newDataset.clientId);
  }, [dispatch, profile]);

  useEffect(() => {
    // make sure dataset and profile has loaded & user is entitled
    if (
      !isDatasetsLoadPending &&
      !isDatasetsLoading &&
      !newDatasetClientId &&
      datasets.size === 0 &&
      !isProfileLoadPending &&
      !isProfileLoading &&
      isUserEntitled
    ) {
      // init new dataSet
      initNewDataset();
    } else if (newDatasetClientId) {
      // select the just-created dataset
      const newDataSet = datasets.find((aDataset) => newDatasetClientId === aDataset.clientId);
      if (newDataSet && newDataSet.id) {
        setDatasetClientId(undefined);
        dispatch(
          authActions.authSelectDataset(
            authTypes.AuthSelectDatasetPayload({ dataset: newDataSet, reload: false, location: '/get-started' }),
          ),
        );
        dispatch(authActions.authSelectDataset(newDataSet));
      }
    }
  }, [
    datasets,
    dispatch,
    initNewDataset,
    isDatasetsLoadPending,
    isDatasetsLoading,
    isUserEntitled,
    isProfileLoadPending,
    isProfileLoading,
    newDatasetClientId,
  ]);

  const [openSelectDatasetDialog, setOpenSelectDatasetDialog] = useState(false);
  const [openTryAgain, setOpenTryAgain] = useState(false);
  const validateData = useCallback(() => {
    if (entitledDatasetsLoadPending) {
      setOpenTryAgain(true);
    } else if (!dataset && datasets.size > 0) {
      setOpenSelectDatasetDialog(true);
    }
  }, [entitledDatasetsLoadPending, dataset, datasets]);
  const [, , resetEntitlementCheck] = useTimeoutFn(validateData, 4000);

  const openFetchDatasetsDialog = useMemo(
    () => isAuthTokenValid && isUserEntitled && (isNoData || openTryAgain) && !dataset,
    [isAuthTokenValid, isUserEntitled, isNoData, openTryAgain, dataset],
  );

  const openOopsDialog = useMemo(
    () => !openFetchDatasetsDialog && !openSelectDatasetDialog && openTryAgain && !dataset,
    [openFetchDatasetsDialog, openSelectDatasetDialog, openTryAgain, dataset],
  );

  return (
    <>
      {openFetchDatasetsDialog && (
        <Dialog
          open
          fullWidth
          maxWidth='md'
          disableEscapeKeyDown
          disableEnforceFocus
          classes={{
            root: classes.dialogStyle,
          }}
        >
          <div className={classes.root}>
            <ZeroStateView
              size='small'
              title='Oops!'
              description={`We're having trouble ${
                isDatasetsLoadPending || datasets.size > 0 ? 'retrieving your dataset' : 'creating a dataset'
              }.`}
              button
              report
              onClick={() => {
                setOpenTryAgain(false);
                if (isDatasetsLoadPending || datasets.size > 0) {
                  dispatch(
                    datasetsActions.getDatasets(undefined, { axiosConfig: { params: { modifiedAfter: null } } }),
                  );
                  resetEntitlementCheck();
                } else {
                  initNewDataset();
                }
              }}
            />
          </div>
        </Dialog>
      )}

      {openSelectDatasetDialog && <SelectDatasetDialog open />}

      {openOopsDialog && (
        <Dialog
          open
          fullWidth
          maxWidth='md'
          disableEscapeKeyDown
          disableEnforceFocus
          classes={{
            root: classes.dialogStyle,
          }}
        >
          <div className={classes.root}>
            <ZeroStateView
              size='small'
              title='Oops!'
              description="We're having trouble retrieving your dataset"
              qIllustration='planeCrash'
              button
              report
              onClick={() => {
                dispatch(authActions.authLogout({ reason: 'AUTH_LOGOUT_SETUP_FAILED' }, { context: 'setup' }));
              }}
            />
          </div>
        </Dialog>
      )}
    </>
  );
};

let attempts = 0;
export const useGetStartedTrigger = () => {
  const gsfScreen = useSelector((state) =>
    preferencesV2Selectors.getPreferenceValue(state, 'gsfScreen', 'completed_gsf'),
  );
  const navigate = useNavigate();
  useEffect(() => {
    if (gsfScreen && gsfScreen !== 'completed_gsf' && window.location.pathname !== '/get-started') {
      if (assert(!attempts, 'attempt to launch GSF more than once per session', attempts)) {
        navigate('/get-started');
      }
      attempts += 1;
    }
  }, [navigate, gsfScreen]);
};

export const Component = React.memo(SetupPageAcme);
export default Component;
